import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LeadsAPI } from "@/lib/api";
import SiteHeader from "@/components/SiteHeader";
import { GoldSpinner } from "@/components/Loaders";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft,
  Trash2,
  Mail,
  Phone,
  Building2,
  ExternalLink,
} from "lucide-react";

const STATUS = [
  { value: "new", label: "Nouveau", color: "#F9DF9F" },
  { value: "contacted", label: "Contacté", color: "#D4AF37" },
  { value: "won", label: "Gagné", color: "#10b981" },
  { value: "lost", label: "Perdu", color: "#71717a" },
];

const PRODUCT_LABELS = {
  player: "Médaille Joueur",
  tournament: "Coupe Tournoi",
  team: "Plaque Équipe",
};

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const list = await LeadsAPI.list();
      setLeads(list);
    } catch {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const setStatus = async (id, status) => {
    try {
      const updated = await LeadsAPI.update(id, { status });
      setLeads((arr) => arr.map((l) => (l.id === id ? updated : l)));
      toast.success("Statut mis à jour");
    } catch {
      toast.error("Échec");
    }
  };

  const remove = async (lead) => {
    if (!window.confirm(`Supprimer la demande de ${lead.full_name} ?`)) return;
    try {
      await LeadsAPI.remove(lead.id);
      setLeads((arr) => arr.filter((l) => l.id !== lead.id));
      toast.success("Supprimé");
    } catch {
      toast.error("Échec");
    }
  };

  const filtered =
    filter === "all" ? leads : leads.filter((l) => l.status === filter);

  const counts = STATUS.reduce(
    (acc, s) => ({
      ...acc,
      [s.value]: leads.filter((l) => l.status === s.value).length,
    }),
    {},
  );

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <SiteHeader />
      <div className="max-w-6xl mx-auto px-4 md:px-8 pt-10 pb-mobile-safe">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 text-zinc-500 hover:text-white text-xs uppercase tracking-[0.2em] mb-6"
        >
          <ArrowLeft className="w-3 h-3" /> Retour Admin
        </Link>

        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-2">
              Pipeline commercial
            </div>
            <h1 className="font-display text-4xl sm:text-5xl font-semibold uppercase tracking-tight text-gradient-gold">
              Demandes de devis
            </h1>
            <p className="text-zinc-400 font-body text-base mt-2">
              Toutes les demandes envoyées via la page{" "}
              <Link
                to="/showcase"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#D4AF37] hover:underline"
              >
                /showcase
              </Link>
              .
            </p>
          </div>
        </div>

        {/* Filter chips */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="leads-filters">
          {[{ value: "all", label: "Tous", color: "#a1a1aa" }, ...STATUS].map(
            (s) => {
              const active = filter === s.value;
              const count =
                s.value === "all" ? leads.length : counts[s.value] || 0;
              return (
                <button
                  key={s.value}
                  onClick={() => setFilter(s.value)}
                  data-testid={`filter-${s.value}`}
                  className="text-[11px] uppercase tracking-[0.18em] font-bold px-4 py-2 rounded-full border transition-all"
                  style={{
                    borderColor: active ? s.color : "rgba(255,255,255,0.15)",
                    background: active ? `${s.color}20` : "transparent",
                    color: active ? "#fff" : "#71717a",
                  }}
                >
                  {s.label}{" "}
                  <span className="ml-1 tabular-nums opacity-60">{count}</span>
                </button>
              );
            },
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center gap-3 text-zinc-500 py-16 font-body">
            <GoldSpinner size={22} />
            <span className="text-[10px] uppercase tracking-[0.3em]">Chargement</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="metal-card rounded-xl p-12 text-center">
            <Mail className="w-10 h-10 mx-auto text-zinc-600 mb-4" />
            <p className="text-zinc-400 font-body">
              {leads.length === 0
                ? "Aucune demande pour le moment. Partage le lien /showcase pour commencer."
                : "Aucune demande dans cette catégorie."}
            </p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="leads-list">
            {filtered.map((l) => {
              const status =
                STATUS.find((s) => s.value === l.status) || STATUS[0];
              return (
                <div
                  key={l.id}
                  data-testid={`lead-row-${l.id}`}
                  className="metal-card rounded-xl p-5"
                >
                  <div className="flex flex-col md:flex-row md:items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 flex-wrap">
                        <h3 className="font-display text-lg font-bold uppercase tracking-tight">
                          {l.full_name}
                        </h3>
                        <span
                          className="text-[10px] uppercase tracking-[0.18em] font-bold px-2.5 py-1 rounded-full border"
                          style={{
                            color: status.color,
                            borderColor: `${status.color}55`,
                            background: `${status.color}15`,
                          }}
                        >
                          {status.label}
                        </span>
                      </div>
                      {l.role && (
                        <div className="text-zinc-500 text-xs font-body mt-1">
                          {l.role}
                          {l.club_name ? ` · ${l.club_name}` : ""}
                        </div>
                      )}
                      <div className="flex flex-wrap gap-x-5 gap-y-1.5 mt-3 text-zinc-300 text-sm font-body">
                        <a
                          href={`mailto:${l.email}`}
                          className="inline-flex items-center gap-1.5 hover:text-white"
                        >
                          <Mail className="w-3 h-3" /> {l.email}
                        </a>
                        {l.phone && (
                          <a
                            href={`tel:${l.phone}`}
                            className="inline-flex items-center gap-1.5 hover:text-white"
                          >
                            <Phone className="w-3 h-3" /> {l.phone}
                          </a>
                        )}
                        {l.club_name && !l.role && (
                          <span className="inline-flex items-center gap-1.5">
                            <Building2 className="w-3 h-3" /> {l.club_name}
                          </span>
                        )}
                      </div>
                      {(l.products?.length > 0 || l.sports?.length > 0) && (
                        <div className="flex flex-wrap gap-1.5 mt-3">
                          {(l.products || []).map((p) => (
                            <span
                              key={p}
                              className="text-[9px] uppercase tracking-[0.15em] font-bold px-2 py-0.5 rounded-full border border-[#D4AF37]/40 text-[#D4AF37] bg-[#D4AF37]/10"
                            >
                              {PRODUCT_LABELS[p] || p}
                            </span>
                          ))}
                          {(l.sports || []).map((s) => (
                            <span
                              key={s}
                              className="text-[9px] uppercase tracking-[0.15em] font-medium px-2 py-0.5 rounded-full border border-white/10 text-zinc-400"
                            >
                              {s}
                            </span>
                          ))}
                        </div>
                      )}
                      {l.quantity_estimate && (
                        <div className="text-zinc-400 text-xs font-body mt-2">
                          <span className="text-zinc-500">Quantité :</span>{" "}
                          {l.quantity_estimate}
                        </div>
                      )}
                      {l.message && (
                        <div className="text-zinc-300 text-sm font-body mt-3 italic border-l-2 border-white/10 pl-3">
                          « {l.message} »
                        </div>
                      )}
                      <div className="text-zinc-600 text-[10px] uppercase tracking-[0.18em] mt-3">
                        Reçu le{" "}
                        {new Date(l.created_at).toLocaleDateString("fr-CH", {
                          day: "2-digit",
                          month: "long",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2 shrink-0">
                      <Select
                        value={l.status}
                        onValueChange={(v) => setStatus(l.id, v)}
                      >
                        <SelectTrigger
                          data-testid={`lead-status-${l.id}`}
                          className="w-36 bg-white/[0.04] border-white/10 text-white text-xs"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUS.map((s) => (
                            <SelectItem key={s.value} value={s.value}>
                              {s.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <a
                        href={`mailto:${l.email}?subject=${encodeURIComponent(
                          "Laser Tech Création - votre demande de devis",
                        )}`}
                        className="inline-flex items-center justify-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold border border-white/15 bg-white/[0.04] text-zinc-300 hover:text-white rounded-md px-3 py-2"
                      >
                        Répondre <ExternalLink className="w-3 h-3" />
                      </a>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => remove(l)}
                        data-testid={`lead-delete-${l.id}`}
                        className="border-red-500/40 bg-red-500/5 text-red-400 hover:bg-red-500/15 h-8"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
